import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader, AdminBody } from "./components/AdminHeader";
import { isImageMedia } from "./components/MediaField";
import { Upload, Trash2, Copy, FileText } from "lucide-react";

const ACCEPT = "image/*,.pdf,.ppt,.pptx,.doc,.docx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const MediaAdmin = () => {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [prog, setProg] = useState(null); // { done, total }

  const { data: items = [], isLoading } = useQuery({ queryKey: ["media"], queryFn: async () => (await api.get("/admin/media")).data });

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    let ok = 0, fail = 0;
    setProg({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch {
        fail += 1;
        toast.error(`Échec : ${file.name}`);
      }
      setProg({ done: i + 1, total: files.length });
    }
    qc.invalidateQueries({ queryKey: ["media"] });
    qc.invalidateQueries({ queryKey: ["admin-media"] });
    if (ok) toast.success(`${ok} fichier${ok > 1 ? "s" : ""} téléversé${ok > 1 ? "s" : ""}${fail ? ` · ${fail} échec${fail > 1 ? "s" : ""}` : ""}`);
    setProg(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/media/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["media"] }); qc.invalidateQueries({ queryKey: ["admin-media"] }); toast.success("Supprimé"); },
  });

  const copy = (url) => { navigator.clipboard.writeText(mediaUrl(url)); toast.success("URL copiée"); };
  const uploading = prog !== null;

  return (
    <>
      <AdminHeader title="Médias" subtitle="Bibliothèque d'images et de documents">
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload"
          className="inline-flex items-center gap-2 bg-f2b-cyan text-f2b-black px-4 py-2 text-sm hover:bg-f2b-cyan/90 disabled:opacity-60">
          <Upload size={16} /> {uploading ? `Téléversement ${prog.done}/${prog.total}…` : "Téléverser"}
        </button>
        <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={upload} data-testid="media-upload-input" />
      </AdminHeader>
      <AdminBody>
        {isLoading ? <p className="text-f2b-gray">Chargement…</p> : items.length === 0 ? (
          <div className="bg-white border border-f2b-filet p-16 text-center text-f2b-gray">Aucun média. Téléversez vos premiers fichiers (images, PDF, PPT, Word).</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {items.map((m) => (
              <div key={m.id} className="bg-white border border-f2b-filet group" data-testid={`media-${m.id}`}>
                <div className="aspect-square overflow-hidden bg-f2b-light flex items-center justify-center">
                  {isImageMedia(m)
                    ? <img src={mediaUrl(m.url)} alt={m.original_filename} className="w-full h-full object-cover" />
                    : <a href={mediaUrl(m.url)} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 text-f2b-gray hover:text-f2b-cyan"><FileText size={40} /><span className="text-[10px] uppercase">{(m.original_filename || "").split(".").pop()}</span></a>}
                </div>
                <div className="p-2">
                  <p className="text-xs text-f2b-gray truncate">{m.original_filename}</p>
                  <div className="flex gap-2 mt-1">
                    <button onClick={() => copy(m.url)} className="text-f2b-gray hover:text-f2b-cyan"><Copy size={14} /></button>
                    <button onClick={() => { if (window.confirm("Supprimer ?")) del.mutate(m.id); }} className="text-f2b-gray hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminBody>
    </>
  );
};

export default MediaAdmin;
